import { createSlice } from '@reduxjs/toolkit';
import { changeLanguage, getCurrentLanguage } from '../../i18n';

// Get initial language from localStorage or default to 'en'
const getInitialLanguage = () => {
  const storedLanguage = localStorage.getItem('i18nextLng');
  if (storedLanguage && ['en', 'nl'].includes(storedLanguage)) {
    return storedLanguage;
  }
  return 'en';
};

const languageSlice = createSlice({
  name: 'language',
  initialState: {
    currentLanguage: getInitialLanguage(),
    isChanging: false,
    error: null,
  },
  reducers: {
    setLanguageStart: (state) => {
      state.isChanging = true;
      state.error = null;
    },
    setLanguageSuccess: (state, action) => {
      state.currentLanguage = action.payload;
      state.isChanging = false;
      state.error = null;
      // Persist to localStorage
      localStorage.setItem('i18nextLng', action.payload);
    },
    setLanguageFailure: (state, action) => {
      state.isChanging = false;
      state.error = action.payload;
    },
    initializeLanguage: (state, action) => {
      state.currentLanguage = action.payload;
      state.error = null;
    },
    clearLanguageError: (state) => {
      state.error = null;
    },
  },
});

// Export actions
export const {
  setLanguageStart,
  setLanguageSuccess,
  setLanguageFailure,
  initializeLanguage,
  clearLanguageError,
} = languageSlice.actions;

// Async action creator for changing language
export const setLanguage = (language) => async (dispatch) => {
  try {
    dispatch(setLanguageStart());
    
    // Validate language
    if (!['en', 'nl'].includes(language)) {
      throw new Error(`Unsupported language: ${language}`);
    }
    
    // Change language in i18next
    await changeLanguage(language);
    
    // Update Redux state
    dispatch(setLanguageSuccess(language));
    
    // Optional: Trigger any additional side effects here
    // For example, you could dispatch actions to refresh certain data
    // that might be language-dependent
    
  } catch (error) {
    dispatch(setLanguageFailure(error.message || 'Failed to change language'));
  }
};

// Thunk to initialize language from browser/localStorage
export const initializeLanguageFromStorage = () => (dispatch) => {
  try {
    const currentLang = getCurrentLanguage();
    dispatch(initializeLanguage(currentLang));
  } catch (error) {
    console.warn('Failed to initialize language from storage:', error);
    dispatch(initializeLanguage('en')); // Fallback to English
  }
};

// Selectors
export const selectCurrentLanguage = (state) => state.language.currentLanguage;
export const selectIsLanguageChanging = (state) => state.language.isChanging;
export const selectLanguageError = (state) => state.language.error;

// Export reducer
export default languageSlice.reducer;
